import React from 'react';
import { useDispatch } from 'react-redux';
import useStyles from './RecordDialogLayoutStyles';

import { FormHelperText, Grid, TextField, Typography } from '@material-ui/core';
import { mdiCar, mdiCoffee, mdiDelete, mdiSilverwareForkKnife } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import DatePickerTypeable from 'components/DatePickerTypeable/DatePickerTypeable';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { setBreakRecordEditAttributes } from 'store/records/actions/records.actions';
import { returnIsFirefoxOrSafari } from 'utils/returnIsFirefoxOrSafari.util';

export const AllowanceRecordsTimePicker = ({
  allowanceRecord,
  disabled,
  parentClockIn,
  parentClockOut,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const dateFormat = 'YYYY-MM-DDTHH:mm';

  const formattedClockInAt = dayjs(allowanceRecord.clockInAt).format(
    dateFormat
  );

  const formattedClockOutAt = dayjs(allowanceRecord.clockOutAt).format(
    dateFormat
  );

  const breakClockInBeforeParentClockIn = formattedClockInAt < parentClockIn;
  const breakClockOutAfterParentClockOut =
    formattedClockOutAt > parentClockOut && allowanceRecord.status !== 'IN';

  const allowanceType = allowanceRecord.type;

  const differenceInMinutes = dayjs(allowanceRecord.clockOutAt)
    .startOf('minute')
    .diff(dayjs(allowanceRecord.clockInAt).startOf('minute'), 'minutes');

  const onDateChange = (value, attributeName) => {
    const utcDate = dayjs(value).toISOString();
    dispatch(
      setBreakRecordEditAttributes({
        id: allowanceRecord.id,
        [attributeName]: utcDate,
      })
    );
  };

  const handleDelete = () => {
    const utcDate = dayjs().toISOString();
    dispatch(
      setBreakRecordEditAttributes({
        id: allowanceRecord.id,
        deletedAt: utcDate,
      })
    );
  };

  const handleClockInGreaterThanClockout = (clockInAt, clockOutAt) => {
    const clockIn = dayjs(clockInAt).toISOString();
    const clockOut = dayjs(clockOutAt).toISOString();

    if (clockIn > clockOut) {
      dispatch(
        setBreakRecordEditAttributes({
          id: allowanceRecord.id,
          clockInAt: clockIn,
          clockOutAt: clockIn,
        })
      );
    }
  };

  const isFirefoxOrSafari = returnIsFirefoxOrSafari();

  const returnAllowanceTypeIcon = () => {
    if (allowanceType === 'BREAK_COFFEE') {
      return <Icon path={mdiCoffee} size={0.8} className={classes.icon} />;
    } else if (allowanceType === 'BREAK_MEAL') {
      return (
        <Icon
          path={mdiSilverwareForkKnife}
          size={0.8}
          className={classes.icon}
        />
      );
    } else if (allowanceType === 'RIDE') {
      return <Icon path={mdiCar} size={0.8} className={classes.icon} />;
    } else {
      return null;
    }
  };
  return (
    <>
      <Grid
        item
        xs={1}
        style={{ padding: 0, textAlign: 'center', margin: 'auto' }}
      >
        {returnAllowanceTypeIcon()}
        <p className={classes.breakTimeDiff}>{`${differenceInMinutes} min`}</p>
      </Grid>
      <Grid item xs={5} className={classes.dateItem}>
        {isFirefoxOrSafari ? (
          <DatePickerTypeable
            id={`break-clock-in-${allowanceRecord.id}`}
            label={t('calendar.start_date')}
            iconVisible
            showTimeSelect
            timeIntervals={5}
            dateFormat="dd.MM.yyyy HH:mm"
            value={formattedClockInAt}
            onChange={(date) => onDateChange(date, 'clockInAt')}
            disabled={disabled}
          />
        ) : (
          <TextField
            id={`break-clock-in-${allowanceRecord.id}`}
            InputLabelProps={{
              shrink: true,
              className: classes.breakTextInput,
            }}
            InputProps={{
              className: `${classes.breakTextInput} ${
                breakClockInBeforeParentClockIn
                  ? classes.clockedInputWarning
                  : ''
              }`,
            }}
            className={classes.breakTextInput}
            fullWidth
            label={t('calendar.start_date')}
            variant="outlined"
            size="small"
            type="datetime-local"
            value={formattedClockInAt}
            onChange={(event) => onDateChange(event.target.value, 'clockInAt')}
            onBlur={(event) =>
              handleClockInGreaterThanClockout(
                event.target.value,
                allowanceRecord.clockOutAt
              )
            }
            disabled={disabled}
          />
        )}
      </Grid>

      <Grid item xs={5} className={classes.dateItem}>
        {allowanceRecord.clockOutAt ? (
          isFirefoxOrSafari ? (
            <DatePickerTypeable
              id={`break-clock-out-${allowanceRecord.id}`}
              label={t('calendar.end_date')}
              iconVisible
              showTimeSelect
              timeIntervals={5}
              dateFormat="dd.MM.yyyy HH:mm"
              value={formattedClockOutAt}
              onChange={(date) => onDateChange(date, 'clockOutAt')}
              minDate={new Date(allowanceRecord.clockInAt)}
              minTime={new Date(allowanceRecord.clockInAt)}
              maxTime={new Date().setHours(23, 59, 59, 999)}
              disabled={disabled}
            />
          ) : (
            <TextField
              id={`break-clock-out-${allowanceRecord.id}`}
              disabled={disabled}
              InputLabelProps={{
                shrink: true,
                className: classes.breakTextInput,
              }}
              InputProps={{
                className: `${classes.breakTextInput} ${
                  breakClockOutAfterParentClockOut
                    ? classes.clockedInputWarning
                    : ''
                }`,
              }}
              fullWidth
              className={classes.breakTextInput}
              label={t('calendar.end_date')}
              variant="outlined"
              size="small"
              type="datetime-local"
              value={formattedClockOutAt}
              onChange={(event) =>
                onDateChange(event.target.value, 'clockOutAt')
              }
              onBlur={(event) =>
                handleClockInGreaterThanClockout(
                  allowanceRecord.clockInAt,
                  event.target.value
                )
              }
            />
          )
        ) : (
          <Typography className={classes.clockedText}>
            {t('calendar.clocked_in')}
          </Typography>
        )}
      </Grid>
      <Grid item xs={1} className={classes.centerItems}>
        <Button
          disabled={disabled}
          elevated
          color="danger"
          style={{ padding: '8px', marginLeft: '8px' }}
          onClick={() => handleDelete()}
        >
          <Icon path={mdiDelete} size={1}></Icon>
        </Button>
      </Grid>

      {breakClockInBeforeParentClockIn || breakClockOutAfterParentClockOut ? (
        <Grid item xs={12} className={classes.centerItems}>
          <FormHelperText className={classes.warningText}>
            {t('calendar.break_is_beyond_record_timeframe')}
          </FormHelperText>
        </Grid>
      ) : null}
    </>
  );
};
